<script setup>
import { ref } from 'vue';

/**
 * Saved views block for the filters sidebar: apply/delete named views, save
 * the current filter combination under a name. State lives in the shared
 * levellingSavedViews composable; `isCurrent` is passed in so highlighting
 * always reflects the live filter state.
 */
const props = defineProps({
  views: { type: Array, required: true },
  isCurrent: { type: Function, required: true },
  canSave: { type: Boolean, default: false }, // any filters active to save?
});

const emit = defineEmits(['apply', 'deactivate', 'remove', 'save']);

const saveOpen = ref(false);
const viewName = ref('');
// Which chip is armed for deletion (shows a confirm affordance).
const pendingDelete = ref(null);

// Clicking a chip toggles it: apply when it isn't the current view, turn the
// filter off (clear) when it already is.
function onChipClick(v) {
  pendingDelete.value = null;
  if (props.isCurrent(v)) emit('deactivate');
  else emit('apply', v);
}
function confirmDelete(v) {
  emit('remove', v.id);
  pendingDelete.value = null;
}

function submit() {
  const name = viewName.value.trim();
  if (!name) return;
  emit('save', name);
  saveOpen.value = false;
  viewName.value = '';
}

function cancel() {
  saveOpen.value = false;
  viewName.value = '';
}
</script>

<template>
  <div class="cc-views">
    <div class="cc-views-head">
      <v-icon size="14">mdi-bookmark-outline</v-icon>
      Saved views
    </div>
    <div v-if="views.length" class="cc-views-list">
      <span v-for="v in views" :key="v.id" class="cc-view-chip" :class="{ 'is-on': isCurrent(v), 'is-confirming': pendingDelete === v.id }">
        <button
          class="cc-view-apply"
          :title="isCurrent(v) ? 'Turn off this view' : 'Apply ' + v.name"
          @click="onChipClick(v)"
        >{{ v.name }}</button>
        <template v-if="pendingDelete === v.id">
          <button class="cc-view-confirm" title="Delete this view" :aria-label="'Confirm delete ' + v.name" @click.stop="confirmDelete(v)">
            <v-icon size="14">mdi-check</v-icon>
          </button>
          <button class="cc-view-keep" title="Keep this view" :aria-label="'Cancel delete ' + v.name" @click.stop="pendingDelete = null">
            <v-icon size="14">mdi-close</v-icon>
          </button>
        </template>
        <button v-else class="cc-view-del" :aria-label="'Delete ' + v.name" @click.stop="pendingDelete = v.id">
          <v-icon size="12">mdi-close</v-icon>
        </button>
      </span>
    </div>
    <p v-else class="cc-views-empty">Save a filter combination to reuse it later.</p>

    <div v-if="saveOpen" class="cc-view-saveform">
      <input
        v-model="viewName"
        class="cc-view-nameinput"
        placeholder="Name this view…"
        @keydown.enter="submit"
        @keydown.esc="cancel"
      />
      <button class="cc-view-savebtn" :disabled="!viewName.trim()" @click="submit">Save</button>
      <button class="cc-view-cancel" @click="cancel">Cancel</button>
    </div>
    <button
      v-else
      class="cc-view-save"
      :disabled="!canSave"
      :title="canSave ? '' : 'Apply a filter first to save it as a view'"
      @click="saveOpen = true"
    >
      <v-icon size="15">mdi-bookmark-plus-outline</v-icon>
      Save current filters
    </button>
  </div>
</template>

<style lang="scss" scoped>
/* Quiet footer section (prototype puts saved views at the foot of the filters,
   separated by a rule — not a boxed card at the top). */
.cc-views {
  padding: 10px 6px 4px;
  margin-top: 6px;
  border-top: 1px solid var(--cc-border-soft, #eef0f3);
}

.cc-views-head {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--cc-text-3, #888888);
  margin-bottom: 8px;
}

.cc-views-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.cc-view-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 999px;
  background: var(--cc-surface-2, #f7f9fb);
  overflow: hidden;

  &.is-on {
    border-color: var(--cc-accent, #1d5bd6);
    background: var(--cc-accent-tint, #e9f0fd);

    .cc-view-apply {
      color: var(--cc-accent, #1d5bd6);
    }
  }

  .cc-view-apply {
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 4px 4px 4px 10px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--cc-text, #333333);
  }

  .cc-view-del {
    display: inline-flex;
    align-items: center;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 4px 8px 4px 2px;
    color: var(--cc-text-3, #999999);

    &:hover {
      color: var(--cc-text, #333333);
    }
  }

  /* Two-step delete: chip turns red-tinted and offers confirm / keep. */
  &.is-confirming {
    border-color: var(--cc-danger, #e5484d);
    background: var(--cc-danger-tint, #fdecec);
  }

  .cc-view-confirm,
  .cc-view-keep {
    display: inline-flex;
    align-items: center;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 4px 4px;
  }

  .cc-view-confirm {
    color: var(--cc-danger, #e5484d);
    padding-left: 6px;
  }

  .cc-view-keep {
    color: var(--cc-text-3, #999999);
    padding-right: 8px;

    &:hover {
      color: var(--cc-text, #333333);
    }
  }
}

.cc-view-save {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--cc-accent, #1976d2);
  padding: 0;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: var(--cc-text-3, #97a0af);
    cursor: default;
  }
}

.cc-views-empty {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
}

.cc-view-saveform {
  display: flex;
  align-items: center;
  gap: 6px;

  .cc-view-nameinput {
    flex: 1;
    min-width: 0;
    border: 1px solid var(--cc-border, #e0e0e0);
    border-radius: 6px;
    padding: 5px 8px;
    font-size: 13px;
    outline: none;

    &:focus {
      border-color: var(--cc-accent, #1d5bd6);
    }
  }

  .cc-view-savebtn {
    border: none;
    border-radius: 6px;
    background: var(--cc-accent, #1d5bd6);
    color: #ffffff;
    font-size: 12.5px;
    font-weight: 600;
    padding: 5px 10px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .cc-view-cancel {
    border: none;
    background: transparent;
    color: var(--cc-text-3, #888888);
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
  }
}
</style>
